/**
 * Modal shell for employment-contract admin customization (Cancel / Preview / Save).
 */
import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../../../../../../config';
import EmploymentContractAdminForm from './EmploymentContractAdminForm';
import {
  defaultEmploymentContractAdminValues,
  employmentContractFromFlatMap,
  employmentContractToFlatMap,
  type EmploymentContractAdminValues,
} from './employmentContractAdminTypes';
import '../InvitationFormEmployerModal.css';
import './EmploymentContractAdminModal.css';

export interface EmploymentContractAdminModalProps {
  isOpen: boolean;
  formCode: string;
  formNameJa: string;
  /** Existing flat map from invitation employer context (draft storage). */
  initialFlat?: Record<string, string>;
  onClose: () => void;
  onSave: (formCode: string, flatFields: Record<string, string>) => void;
}

const EmploymentContractAdminModal: React.FC<EmploymentContractAdminModalProps> = ({
  isOpen,
  formCode,
  formNameJa,
  initialFlat,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState<EmploymentContractAdminValues>(
    defaultEmploymentContractAdminValues()
  );
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(employmentContractFromFlatMap(initialFlat));
    setPreviewError(null);
  }, [isOpen, initialFlat]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave(formCode, employmentContractToFlatMap(draft));
    onClose();
  };

  const handlePreview = async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/employment-contract/admin-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employmentContractToFlatMap(draft)),
      });
      if (!res.ok) {
        throw new Error(`Preview failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later so the new tab can load the blob
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'プレビューに失敗しました');
    } finally {
      setPreviewBusy(false);
    }
  };

  return (
    <div className="inv-form-employer-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="inv-form-employer-modal emp-contract-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="emp-contract-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="inv-form-employer-modal__header">
          <h2 id="emp-contract-modal-title" className="inv-form-employer-modal__title">
            管理者入力
          </h2>
          <button
            type="button"
            className="inv-form-employer-modal__close-btn"
            onClick={onClose}
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        <div className="inv-form-employer-modal__body emp-contract-modal__body">
          <p className="inv-form-employer-modal__form-name">{formNameJa}</p>
          <p className="inv-form-employer-modal__hint">
            雇入通知書兼雇用契約書の内容を設定します。プレビューでPDFを確認してから保存できます。
          </p>
          <EmploymentContractAdminForm value={draft} onChange={setDraft} />
          {previewError && (
            <p className="emp-contract-modal__error" role="alert">
              {previewError}
            </p>
          )}
        </div>

        <div className="inv-form-employer-modal__footer">
          <button
            type="button"
            className="inv-form-employer-modal__cancel-btn"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="emp-contract-modal__preview-btn"
            onClick={() => void handlePreview()}
            disabled={previewBusy}
          >
            {previewBusy ? 'プレビュー中…' : 'プレビュー'}
          </button>
          <button
            type="button"
            className="inv-form-employer-modal__save-btn"
            onClick={handleSave}
          >
            保存
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmploymentContractAdminModal;
